// [NEW FILE] HR screen for the reporting-manager resolution queue (backend migration 010).
// Opened from the banner / "Unmatched managers" button on pages/hr/EmployeeMasterPage.jsx.
import { useState } from "react";
import { toast } from "react-toastify";
import { FiCheck, FiSlash } from "react-icons/fi";
import { Modal, Button, DataTable, PersonCell, FormField, Banner, StatusBadge, MultiSelect } from "../../../components/common";
import useAsync from "../../../hooks/useAsync";
import { listReportingResolutions, resolveReporting, dismissReporting } from "../../../services/reportingResolutionApi";
import { nameOf } from "../../../services/core";
import { formatDate } from "../../../utils/format";
import { getErrorMessage } from "../../../utils/errors";
import Select from "../../../components/common/Select"; // [NEW] alphabetical dropdown (replaces <select>)

const STATUS_LABEL = { PENDING: "Pending", RESOLVED: "Resolved", DISMISSED: "Dismissed" };

export default function ReportingResolutionsModal({ open, managers = [], onClose, onChanged }) {
  // [UPDATED] PM-18: status is a checkbox filter (default Pending). Each chosen status is loaded and the lists merged.
  const [statuses, setStatuses] = useState(["PENDING"]);
  const statusKey = statuses.join(",");
  const list = useAsync(async () => {
    const wanted = statuses.length ? statuses : Object.keys(STATUS_LABEL);
    const parts = await Promise.all(wanted.map((s) => listReportingResolutions(s)));
    return { data: parts.flatMap((p) => p.data || p) };
  }, [statusKey, open], { skip: !open }); // eslint-disable-line react-hooks/exhaustive-deps
  const onlyPending = statuses.length === 1 && statuses[0] === "PENDING";
  const [action, setAction] = useState(null); // { row, mode: "resolve" | "dismiss", managerId, effectiveFrom, reason, error }
  const [busy, setBusy] = useState(false);

  const start = (row, mode) => setAction({ row, mode, managerId: "", effectiveFrom: "", reason: "", error: "" });

  const submit = async () => {
    if (action.mode === "resolve" && !action.managerId) {
      setAction((a) => ({ ...a, error: "Choose the reporting manager." }));
      return;
    }
    if (action.mode === "dismiss" && !action.reason.trim()) {
      setAction((a) => ({ ...a, error: "Write why this employee has no reporting manager." }));
      return;
    }
    setBusy(true);
    try {
      if (action.mode === "resolve") {
        await resolveReporting(action.row.id, action);
        toast.success(`${action.row.employeeName} now reports to ${nameOf(action.managerId)}.`);
      } else {
        await dismissReporting(action.row.id, action.reason);
        toast.success(`Saved: ${action.row.employeeName} has no reporting manager.`);
      }
      setAction(null);
      list.reload();
      onChanged?.();
    } catch (err) {
      setAction((a) => ({ ...a, error: getErrorMessage(err) }));
    } finally {
      setBusy(false);
    }
  };

  const options = managers.filter((m) => m.id !== action?.row?.employeeId);

  return (
    <Modal
      open={open}
      size="lg"
      title="Unmatched reporting managers"
      subtitle="The import file named a manager that matched nobody (or more than one person). Confirm the right manager, or record that there is none."
      onClose={busy ? undefined : onClose}
    >
      <div className="filter-bar">
        <MultiSelect label="Status" allLabel="Any status" keepOrder options={Object.entries(STATUS_LABEL).map(([k, v]) => ({ value: k, label: v }))} value={statuses} onChange={(v) => { setStatuses(v); setAction(null); }} />
      </div>

      {list.error && <Banner tone="danger" title="Could not load the list">{list.error}</Banner>}

      {action && (
        <div className="card card-body form-stack resolution-form">
          <p className="banner-title">
            {action.mode === "resolve" ? "Confirm manager for " : "No manager for "}{action.row.employeeName} ({action.row.employeeId})
          </p>
          <p className="form-hint">Name in the import file: <strong>{action.row.managerNameInFile}</strong></p>
          {action.mode === "resolve" && (
            <>
              <FormField label="Reporting manager" required>
                <Select value={action.managerId} onChange={(e) => setAction((a) => ({ ...a, managerId: e.target.value, error: "" }))}>
                  <option value="">Choose a manager</option>
                  {options.map((m) => <option key={m.id} value={m.id}>{m.name} ({m.id})</option>)}
                </Select>
              </FormField>
              <FormField label="Reports from (optional)" hint="Leave empty to use the later joining date of the two people.">
                <input type="date" value={action.effectiveFrom} onChange={(e) => setAction((a) => ({ ...a, effectiveFrom: e.target.value }))} />
              </FormField>
            </>
          )}
          <FormField label={action.mode === "dismiss" ? "Reason" : "Reason (optional)"} required={action.mode === "dismiss"} hint="Saved in the audit trail.">
            <input value={action.reason} maxLength={1000} onChange={(e) => setAction((a) => ({ ...a, reason: e.target.value, error: "" }))} />
          </FormField>
          {action.error && <p className="form-error">{action.error}</p>}
          <span className="row-actions">
            <Button variant="ghost" onClick={() => setAction(null)} disabled={busy}>Cancel</Button>
            <Button variant={action.mode === "dismiss" ? "danger" : "primary"} onClick={submit} loading={busy}>
              {action.mode === "resolve" ? "Save manager" : "Save: no manager"}
            </Button>
          </span>
        </div>
      )}

      <DataTable
        loading={list.loading && !list.data}
        rows={list.data || []}
        emptyTitle={onlyPending ? "Nothing to resolve" : "No matching items"}
        emptyText={onlyPending ? "Every imported manager name was matched." : ""}
        columns={[
          { key: "employee", label: "Employee", render: (r) => <PersonCell name={r.employeeName} meta={r.employeeId} /> },
          { key: "managerNameInFile", label: "Manager name in file" },
          { key: "createdAt", label: "Imported", render: (r) => formatDate(r.createdAt) },
          {
            key: "actions", label: onlyPending ? "Action" : "Action / outcome",
            render: (r) => (r.status === "PENDING"
              ? (
                <span className="row-actions">
                  <Button variant="ghost" size="sm" icon={FiCheck} onClick={() => start(r, "resolve")}>Set manager</Button>
                  <Button variant="ghost" size="sm" icon={FiSlash} onClick={() => start(r, "dismiss")}>No manager</Button>
                </span>
              )
              : (
                <>
                  <StatusBadge status={STATUS_LABEL[r.status]} />
                  {r.resolvedManagerId && <div className="muted">Reports to {nameOf(r.resolvedManagerId)}</div>}
                  {r.note && <div className="muted">{r.note}</div>}
                </>
              )),
          },
        ]}
      />
    </Modal>
  );
}
